import { useEffect } from 'react';
import type { Location } from 'react-router-dom';
import type {
  DrawerStateFromHome,
  DrawerState,
  ShipmentFiltersPayload,
  ToggleDrawerFn,
} from '../types';

interface UseShipmentsEffectsParams {
  location: Location;

  drawerStateFromHome: DrawerStateFromHome;
  setDrawerStateFormHome: React.Dispatch<
    React.SetStateAction<DrawerStateFromHome>
  >;

  page: number;
  pageSize: number;
  isSearchVisible: boolean;
  searchQuery: string;

  startAlertsInterval: boolean;
  pollIntervalMs: number;

  fetchShipments: (
    searchquery: string,
    check: boolean,
    pageArg?: number,
    pageSizeArg?: number,
    calledFrom?: string,
    useMissingIds?: boolean,
    missingIdsOverride?: string[] | null,
    payloadOverride?: ShipmentFiltersPayload | null,
    tabIdOverride?: string | null,
    sortKeyOverride?: string,
    sortOrderOverride?: 'ascending' | 'descending'
  ) => Promise<unknown> | void;

  fetchShipmentSummary: (
    runSpin?: boolean,
    tabId?: string
  ) => Promise<unknown> | void;

  refreshShipmentsTick: () => Promise<unknown> | void;
  refreshSummaryTick: () => Promise<unknown> | void;

  drawerState: DrawerState;
  setDrawerState?: React.Dispatch<React.SetStateAction<DrawerState>>;
  toggleDrawer: ToggleDrawerFn;

  // refs
  pageRef: React.MutableRefObject<number>;
  pageSizeRef: React.MutableRefObject<number>;
  isTabVisibleRef: React.MutableRefObject<boolean>;
  searchVisibleRef: React.MutableRefObject<boolean>;
  searchQueryRef: React.MutableRefObject<string>;
  didRunInitialFetchRef: React.MutableRefObject<boolean>;
  didInitialQueryStringEffect: React.MutableRefObject<boolean>;
  fetchRef: React.MutableRefObject<
    (
      searchquery: string,
      check: boolean,
      pageArg?: number,
      pageSizeArg?: number,
      calledFrom?: string,
      useMissingIds?: boolean,
      missingIdsOverride?: string[] | null,
      payloadOverride?: ShipmentFiltersPayload | null,
      tabIdOverride?: string | null,
      sortKeyOverride?: string,
      sortOrderOverride?: 'ascending' | 'descending'
    ) => Promise<unknown> | void
  >;
  initialFetchEnabled?: boolean;
}

export const useShipmentsEffects = ({
  location,
  drawerStateFromHome,
  setDrawerStateFormHome,
  page,
  pageSize,
  isSearchVisible,
  searchQuery,
  startAlertsInterval,
  pollIntervalMs,
  fetchShipments,
  fetchShipmentSummary,
  refreshShipmentsTick,
  refreshSummaryTick,
  drawerState,
  toggleDrawer,
  pageRef,
  pageSizeRef,
  isTabVisibleRef,
  searchVisibleRef,
  searchQueryRef,
  didRunInitialFetchRef,
  didInitialQueryStringEffect,
  fetchRef,
  initialFetchEnabled = true,
}: UseShipmentsEffectsParams) => {
  useEffect(() => {
    if (location.state) {
      setDrawerStateFormHome({
        dState: (location.state as DrawerStateFromHome)?.dState ?? '',
        shipmentId: (location.state as DrawerStateFromHome)?.shipmentId ?? null,
        cocId: (location.state as DrawerStateFromHome)?.cocId ?? null,
      });
    }
  }, [location.key, location.state, setDrawerStateFormHome]);

  useEffect(() => {
    pageRef.current = page;
  }, [page, pageRef]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize, pageSizeRef]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
    searchVisibleRef.current = isSearchVisible;
  }, [searchQuery, isSearchVisible, searchQueryRef, searchVisibleRef]);

  useEffect(() => {
    if (!startAlertsInterval) return;

    const intervalId = setInterval(() => {
      refreshShipmentsTick();
      refreshSummaryTick();
    }, pollIntervalMs);

    return () => clearInterval(intervalId);
  }, [
    startAlertsInterval,
    pollIntervalMs,
    refreshShipmentsTick,
    refreshSummaryTick,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isTabVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        refreshShipmentsTick();
        refreshSummaryTick();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isTabVisibleRef, refreshShipmentsTick, refreshSummaryTick]);

  useEffect(() => {
    if (!initialFetchEnabled) return;
    if (didRunInitialFetchRef.current) return;
    didRunInitialFetchRef.current = true;

    fetchShipments(searchQueryRef.current, true);
    fetchShipmentSummary(true);
  }, [
    didRunInitialFetchRef,
    fetchShipments,
    fetchShipmentSummary,
    initialFetchEnabled,
    searchQueryRef,
  ]);

  useEffect(() => {
    fetchRef.current = fetchShipments;
  }, [fetchRef, fetchShipments]);

  useEffect(() => {
    if (!didInitialQueryStringEffect.current) {
      didInitialQueryStringEffect.current = true;
      return;
    }

    fetchRef.current(searchQueryRef.current, false);
  }, [didInitialQueryStringEffect, fetchRef, searchQueryRef]);

  useEffect(() => {
    if (
      drawerStateFromHome &&
      drawerStateFromHome.dState === 'open' &&
      drawerStateFromHome.shipmentId
    ) {
      toggleDrawer(
        drawerStateFromHome.dState as 'open' | 'close',
        'Detail',
        drawerStateFromHome.shipmentId
      );
    }
  }, [drawerStateFromHome, toggleDrawer]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shipmentId = params.get('shipment_id');
    const legType = (params.get('leg_type') || params.get('legType')) as
      'CL' | 'EL' | undefined;
    if (shipmentId) {
      toggleDrawer('open', 'Detail', shipmentId, null, legType);
    }
  }, [location.search, toggleDrawer]);

  useEffect(() => {
    if (drawerState.isOpen && drawerState.name === 'notifications') {
      document.documentElement.classList.add('hide-scrollbar');
    } else {
      document.documentElement.classList.remove('hide-scrollbar');
    }
  }, [drawerState.isOpen, drawerState.name]);
};
